import {BehaviorSubject, combineLatest, Observable} from 'rxjs';
import {
  EventEmitter,
  Injectable
}                                                   from '@angular/core';
import {
  ObjectFactory
}                                                   from 'ts-tooling';
import {
  filter,
  map
}                                                   from 'rxjs/operators';
import {
  ApDynGridColumnConfigBase
}                                                   from '../../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridPagerConfigBase
}                                                   from '../../../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {
  FieldStore
}                                                   from '../../../../stores/farm/field.store';
import {
  ApDynGridPagerWizardBreadcrumbsStepper,
  ApDynGridPagerWizardButton,
  ApDynGridPagerWizardConfig
}                                                   from '../../../../ap-dyngrids/config/ap-dyn-grid-pager-wizard-config';
import {
  ApDynformsConfigFieldset
}                                                   from '../../../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {
  ApDynComponentComponent
}                                                   from '../../../../ap-dyncomponent/ap-dyncomponent.component';
import {
  LanguageStore
}                                                   from '../../../../stores/translation/language.store';
import {
  SettingsStore
}                                                   from '../../../../stores/base-data/settings.store';
import {
  IGridStatus
}                                                   from '../../../../ap-interface/interfaces/ap-grid-status.interface';
import {
  ApColumnState
}                                                   from '../../../../ap-interface/enums/ap-column-state.enum';
import {
  ApGetCropService
}                                                   from '../../../../stores/services/ap-get-crop.service';
import {
  FieldService
}                                                   from '../../../../field-management/components/field.service';
import {
  CampaignYearStore
}                                                   from '../../../../stores/login/campaignyear.store';
import {
  FwPlanPlanningSelectionConfig
}                                                   from '../configs/fw-plan-planning-selection.config';
import {
  FwPlanningWizardStates,
  IFwPlanPlanningWizardDetailsData,
  IFwPlanPlanningWizardProvider,
  IFwPlanPlanningWizardSelectionData,
  IFwPlanPlanningWizardSelectionProvider
}                                                   from '../fw-plan-planning.types';
import ICampaignYear = Data.Authentication.ICampaignYear;
import IFieldGeom = Data.FieldManagement.IFieldGeom;

@Injectable()
export class FwPlanPlanningSelectionProvider implements IFwPlanPlanningWizardSelectionProvider,
  IFwPlanPlanningWizardProvider<IFwPlanPlanningWizardSelectionData, IFwPlanPlanningWizardSelectionData[]> {

  private _caption$: BehaviorSubject<string>;
  private _fieldSets$: BehaviorSubject<ApDynformsConfigFieldset[]>;
  private _handleStateChange: EventEmitter<FwPlanningWizardStates>;
  private _dynComponent: ApDynComponentComponent;

  constructor(private fieldStore: FieldStore,
              private languageStore: LanguageStore,
              private settingsStore: SettingsStore,
              private campaignYearStore: CampaignYearStore,
              private fieldService: FieldService,
              private cropService: ApGetCropService,
              private fwPlanPlanningWizardSelectionUiProvider: FwPlanPlanningSelectionConfig) {
  }

  /**
   * Initializes the provider with necessary parts and observables
   * @param caption$ - BehaviorSubject for the caption text
   * @param fieldSets$ - BehaviorSubject for the form fieldsets
   * @param handleStateChange - EventEmitter for handling wizard state changes
   * @param dynComponent - Reference to the dynamic component
   * @public
   */
  public init(caption$: BehaviorSubject<string>,
              fieldSets$: BehaviorSubject<ApDynformsConfigFieldset[]>,
              handleStateChange: EventEmitter<FwPlanningWizardStates>,
              dynComponent: ApDynComponentComponent): void {
    this._caption$ = caption$;
    this._fieldSets$ = fieldSets$;
    this._handleStateChange = handleStateChange;
    this._dynComponent = dynComponent;
  }

  /**
   * Builds data items for field selection by combining fields and campaign year data
   * @returns Observable that emits an array of field selection data items containing field details,
   * status, and crop information
   * @public
   */
  public buildDataItems(): Observable<IFwPlanPlanningWizardSelectionData[]> {
    return combineLatest([
      this.fieldStore.Fields$,
      this.campaignYearStore.SelectedCampaignYear$
    ]).pipe(
      filter(([fields, selectedCampaignYear]) => fields.length > 0 && !!selectedCampaignYear),
      map(([fields, selectedCampaignYear]) => {
        return fields.map(field => {
          const fieldGeom = this.fieldStore.getCurrentFieldGeom(field);
          const fieldCombinedNumber = this.fieldStore.getFieldNumberAndSubNumber(field);
          const mainCropDescription = this.cropService.parseMainCrop(field);
          const status = this.getItemGridStatus(fieldGeom, selectedCampaignYear, mainCropDescription);
          return {
            Id: field.Id,
            Status: status,
            FieldName: field.FieldName,
            FieldNumber: field.FieldNumber,
            FieldSecondNumber: field.FieldSubnumber,
            FieldCombinedNumber: fieldCombinedNumber,
            FieldGeomId: fieldGeom?.Id,
            Area: fieldGeom?.AreaHa,
            CropName: mainCropDescription
          } as IFwPlanPlanningWizardSelectionData;
        });
      })
    );
  }

  /**
   * Handles the grid selection change event by updating the selected fields in the fieldStore
   * @param items - Array of selected field data items containing field IDs
   * @public
   */
  public onGridSelectionChanged(items: IFwPlanPlanningWizardSelectionData[]): void {
    this.fieldStore.changeSelectedField(items.map((item) => item?.Id?.toString()));
  }

  /**
   * Called when leaving the selection page, updates selected fields in the store and returns a copy of selected items
   * @returns Array of selected field data items
   * @public
   */
  public onLeave(): IFwPlanPlanningWizardSelectionData[] {
    this.fieldStore.changeSelectedField(this._dynComponent.dynGrid.gridPaging.selectedKeys?.map(k => k.toString()));
    const selectedFields = this._dynComponent.dynGrid.gridPaging.selectedItems as IFwPlanPlanningWizardSelectionData[];
    return ObjectFactory.Copy(selectedFields);
  }

  /**
   * Cleans up the component by resetting the selected fields in the store
   * @public
   */
  public onDestroy(): void {
    this.fieldStore.changeSelectedField([]);
  }

  /**
   * Updates the caption text in the wizard to the field selection title
   * @public
   */
  public updateCaption(): void {
    this._caption$.next('Nutrients__SelectFieldsTitle');
  }

  /**
   * Updates the form fieldsets by resetting them to an empty array
   * @public
   */
  public updateFieldSets(): void {
    this._fieldSets$.next([]);
  }

  /**
   * Gets the grid column configuration for the selection grid
   * @param _ - Unused details data parameter
   * @returns Observable that emits an array of grid column configuration objects based on the selected language and settings
   * @public
   */
  public getGridColumnConfig(_: IFwPlanPlanningWizardDetailsData): Observable<ApDynGridColumnConfigBase[]> {
    return combineLatest([
      this.languageStore.SelectedLanguage$,
      this.settingsStore.FirstSetting$
    ]).pipe(
      filter(([language, setting]) => !!language && !!setting),
      map(([_1, setting]) => {
        return this.fwPlanPlanningWizardSelectionUiProvider.generateColumns(setting);
      })
    );
  }

  /**
   * Gets the wizard pager configuration with cancel button, breadcrumb stepper, and next button
   * @param breadCrumbsColumns Array of wizard states to show as breadcrumb steps
   * @returns Pager configuration object with buttons and breadcrumbs layout
   * @public
   */
  public getPagerConfig(breadCrumbsColumns: FwPlanningWizardStates[]): ApDynGridPagerConfigBase {
    return new ApDynGridPagerWizardConfig(
      new ApDynGridPagerWizardButton('Global__Cancel', this._handleStateChange,
        {value: FwPlanningWizardStates.Cancel}),
      new ApDynGridPagerWizardBreadcrumbsStepper(breadCrumbsColumns, 0),
      new ApDynGridPagerWizardButton('Global_Next_With_Arrow', this._handleStateChange,
        {
          active: this._dynComponent.SelectedItems$.pipe(map(x => x?.length > 0)),
          value: FwPlanningWizardStates.Details,
          primary: true
        })
    );
  }

  /**
   * Determines the grid status of an item based on field geometry, campaign year, and crop description
   * @param fieldGeom - Field geometry data containing validation dates
   * @param campaignYear - Campaign year data with default start and end dates
   * @param mainCropDescription - Description of the main crop, if available
   * @returns IGridStatus object containing state, CSS class, and description based on validation rules
   * @public
   */
  private getItemGridStatus(fieldGeom: IFieldGeom, campaignYear: ICampaignYear, mainCropDescription: string | undefined): IGridStatus {
    const geomStatus = this.fieldService.getGeomStatus(fieldGeom, campaignYear);
    if (geomStatus === -1) {
      return {
        state: ApColumnState.Red,
        class: 'ap-status-red',
        description: 'Global__Please_Contact_The_Support'
      };
    }
    if (!!mainCropDescription) {
      return {
        state: ApColumnState.Green,
        class: 'ap-status-green',
        description: ''
      };
    }
    return {
      state: ApColumnState.Yellow,
      class: 'ap-status-yellow',
      description: ''
    };
  }
}
